import React from 'react';
import PropTypes from 'prop-types';
import { costFormatter } from '@format/currency';
import {
  DescriptionList,
  DescriptionListLabel,
  DescriptionListValue,
  HorizontalRule,
} from '@web/atoms';
import { BoldText } from './styles.js';
import { PricingListDisplayFreeItems, PromoCodeInfo } from './components';

export const PricingList = ({
  puptainerLabel,
  shippingLabel,
  scooperLabel,
  bowlLabel = 'Bowl',
  foodSubtotalLabel,
  foodSubtotal,
  addonsSubtotal,
  addonsSubtotalLabel,
  discountLabel,
  discountText,
  discountAmount,
  taxLabel,
  tax,
  totalLabel,
  total,
  plans,
  promoCodeInfo,
  showPromoCode,
  applyPromoCode,
  removePromoCode,
  isDefaultPromoCode,
  currentOfferCode,
  freeItems,
}) => {
  const planHasAddons = parseInt(addonsSubtotal) > 0;
  return (
    <>
      <DescriptionList title="additional items and costs">
        <PricingListDisplayFreeItems
          plans={plans}
          freeItems={freeItems}
          puptainerLabel={puptainerLabel}
          scooperLabel={scooperLabel}
          bowlLabel={bowlLabel}
        />
        <DescriptionListLabel>{shippingLabel}</DescriptionListLabel>
        <DescriptionListValue>
          <BoldText color="status.successPrimary">FREE</BoldText>
        </DescriptionListValue>
      </DescriptionList>
      <HorizontalRule />
      {showPromoCode && (
        <>
          <PromoCodeInfo
            {...promoCodeInfo}
            applyPromoCode={applyPromoCode}
            removePromoCode={removePromoCode}
            isDefaultPromoCode={isDefaultPromoCode}
          />
          <HorizontalRule />
        </>
      )}

      <DescriptionList title="tax, discounts and subtotals">
        <DescriptionListLabel>{foodSubtotalLabel}</DescriptionListLabel>
        <DescriptionListValue>
          <BoldText>{costFormatter(foodSubtotal)}</BoldText>
        </DescriptionListValue>

        {planHasAddons && (
          <>
            <DescriptionListLabel>{addonsSubtotalLabel}</DescriptionListLabel>
            <DescriptionListValue>
              <BoldText>{costFormatter(addonsSubtotal)}</BoldText>
            </DescriptionListValue>
          </>
        )}

        {showPromoCode === false && (
          <>
            <DescriptionListLabel color="hero.spinach">
              {discountLabel + ' '}
              <BoldText color="hero.spinach">{discountText}</BoldText>
            </DescriptionListLabel>
            <DescriptionListValue>
              <BoldText color="hero.spinach">
                {discountAmount && `-${costFormatter(discountAmount)}`}
              </BoldText>
            </DescriptionListValue>
          </>
        )}
        {showPromoCode && (
          <>
            <DescriptionListLabel color="status.successPrimary">
              <BoldText color="status.successPrimary">
                {`${currentOfferCode} `}
              </BoldText>
              ({discountText})
            </DescriptionListLabel>
            <DescriptionListValue>
              <BoldText color="status.successPrimary">
                {discountAmount && `-${costFormatter(discountAmount)}`}
              </BoldText>
            </DescriptionListValue>
          </>
        )}

        {tax && (
          <>
            <DescriptionListLabel>{taxLabel}</DescriptionListLabel>
            <DescriptionListValue>
              <BoldText>{costFormatter(tax)}</BoldText>
            </DescriptionListValue>
          </>
        )}
      </DescriptionList>
      <HorizontalRule />
      <DescriptionList title="total cost">
        <DescriptionListLabel>
          <BoldText>{totalLabel}</BoldText>
        </DescriptionListLabel>
        <DescriptionListValue>
          <BoldText>{total && costFormatter(total)}</BoldText>
        </DescriptionListValue>
      </DescriptionList>
    </>
  );
};
PricingList.displayName = 'PricingList';
PricingList.propTypes = {
  puptainerLabel: PropTypes.string.isRequired,
  shippingLabel: PropTypes.string.isRequired,
  scooperLabel: PropTypes.string.isRequired,
  bowlLabel: PropTypes.string,
  foodSubtotalLabel: PropTypes.string.isRequired,
  addonsSubtotalLabel: PropTypes.string.isRequired,
  foodSubtotal: PropTypes.string,
  addonsSubtotal: PropTypes.string,
  discountLabel: PropTypes.string.isRequired,
  discountText: PropTypes.string,
  discountAmount: PropTypes.string,
  taxLabel: PropTypes.string.isRequired,
  tax: PropTypes.string,
  totalLabel: PropTypes.string.isRequired,
  total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      breeds: PropTypes.arrayOf(PropTypes.string).isRequired,
      petId: PropTypes.string.isRequired,
      price: PropTypes.string.isRequired,
      priceWithDiscount: PropTypes.string.isRequired,
      recipes: PropTypes.object.isRequired,
      numPacks: PropTypes.number.isRequired,
      bagCount: PropTypes.number.isRequired,
      productType: PropTypes.string,
    }),
  ),
  promoCodeInfo: PropTypes.shape({
    promoCodeLabel: PropTypes.string.isRequired,
    changePromoCodeLabel: PropTypes.string.isRequired,
    applyPromoCodeLabel: PropTypes.string.isRequired,
    removePromoCodeLabel: PropTypes.string.isRequired,
    errorPromoCodeLabel: PropTypes.string,
    promoCodeAppliedLabel: PropTypes.string,
    newPromoCodeLabel: PropTypes.string,
  }),
  showPromoCode: PropTypes.bool.isRequired,
  applyPromoCode: PropTypes.func.isRequired,
  removePromoCode: PropTypes.func.isRequired,
  isDefaultPromoCode: PropTypes.bool.isRequired,
  currentOfferCode: PropTypes.string.isRequired,
  freeItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
    }),
  ),
};
